import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { tokens, surface, hairline } from '../../../styles/designTokens';
import { FiX, FiAlertCircle, FiDownload, FiCheckCircle, FiCheck, FiPlay, FiFileText, FiFilter, FiKey, FiCalendar, FiRepeat, FiSearch, FiClock, FiPackage, FiXCircle } from 'react-icons/fi';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { MdOutlineNumbers } from 'react-icons/md';
import { HiOutlineInbox } from 'react-icons/hi';
import { GoCheck, GoSync } from 'react-icons/go';
import api from '../../../services/api';
import { toast } from 'react-toastify';

export default function DownloadKeysModal({ isOpen, onClose, onDownload, onComplete, progressLogs = [], currentProgress = null, isProcessing = false }) {
  const [keysList, setKeysList] = useState('');
  const [processing, setProcessing] = useState(false);
  const [taskProgress, setTaskProgress] = useState(0);
  const [taskStage, setTaskStage] = useState('');
  const [taskStats, setTaskStats] = useState([]);
  const [taskLogs, setTaskLogs] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState('keys'); // 'keys' ou 'filter'
  const [isDownloading, setIsDownloading] = useState(false);
  const [fileDownloadProgress, setFileDownloadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [downloadLink, setDownloadLink] = useState('');
  const [currentTaskType, setCurrentTaskType] = useState('download_xml_keys'); // Rastrear tipo de processamento
  const [currentOutputId, setCurrentOutputId] = useState(null); // Armazenar outputId do processamento
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  // Estados dos filtros
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [modelo, setModelo] = useState([]);
  const [cfopList, setCfopList] = useState([]);
  const [cfopInput, setCfopInput] = useState('');
  const [direcao, setDirecao] = useState(null);
  const [status, setStatus] = useState(null);

  // Conectar WebSocket para receber atualizações em tempo real
  const connectWebSocket = (projectId, taskType = 'download_xml_keys') => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Salvar tipo de tarefa
    setCurrentTaskType(taskType);
    
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    
    if (!token) {
      toast.error('Token de autenticação não encontrado');
      return;
    }
    
    const ws = new WebSocket(`wss://log.vidal-app.com/ws?token=${token}`);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('✅ WebSocket conectado para tipo:', taskType);
      
      // Inscrever-se para receber mensagens
      ws.send(JSON.stringify({
        action: 'subscribe',
        projectId,
        type: taskType
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        
        if (!parsed.success || !parsed.message) return;
        
        const msg = parsed.message;
        // Suportar ambos os tipos
        if ((msg.type !== 'download_xml_keys' && msg.type !== 'download_xml_filter') || msg.projectId !== projectId) return;
        
        const { progress, stage, message, stats, status, totalChaves, arquivosEncontrados, arquivosFaltantes, downloadLink: link } = msg.data;
        
        // Atualizar estado
        if (progress !== undefined && !isNaN(progress)) setTaskProgress(progress);
        if (stage) setTaskStage(stage);
        if (link) setDownloadLink(link);
        
        // Stats pode vir como array ou objeto, normalizar
        if (stats && Array.isArray(stats)) {
          // Se for filtro, sempre remover KPI de Faltantes
          if (msg.type === 'download_xml_filter') {
            setTaskStats(stats.filter(s => s.label !== 'Faltantes'));
          } else {
            setTaskStats(stats);
          }
        } else if (totalChaves !== undefined) {
          // Criar stats a partir dos campos brutos
          const allStats = [
            { label: 'Total Solicitadas', value: totalChaves || 0, type: 'info' },
            { label: 'Encontradas', value: arquivosEncontrados || 0, type: 'success' },
            { label: 'Faltantes', value: arquivosFaltantes || 0, type: 'error' }
          ];
          
          // Se for filtro, sempre remover KPI de Faltantes
          if (msg.type === 'download_xml_filter') {
            setTaskStats(allStats.filter(s => s.label !== 'Faltantes'));
          } else {
            setTaskStats(allStats);
          }
        }
        
        // Adicionar log
        if (message) {
          setTaskLogs(prev => [...prev, {
            message,
            timestamp: new Date().toLocaleTimeString('pt-BR'),
            type: status === 'error' ? 'error' : status === 'completed' ? 'success' : 'info',
            progress
          }]);
        }
        
        // Debug: Log para verificar status
        console.log('[DownloadKeysModal] WebSocket message:', {
          progress,
          stage,
          status,
          message
        });
        
        // Processamento concluído - Verificar múltiplas condições
        if (status === 'completed' || progress >= 100) {
          console.log('[DownloadKeysModal] ✅ Processamento concluído detectado!');
          setProcessing(false);
          setTaskProgress(100);
          toast.success('Download de XMLs concluído!', {
            autoClose: 5000
          });
          
          // Atualizar lista de downloads após conclusão
          if (onComplete) {
            setTimeout(() => {
              onComplete();
            }, 1000); // Aguardar 1s para garantir que ES foi indexado
          }
        }
        
        if (status === 'error') {
          console.log('[DownloadKeysModal] ❌ Erro detectado:', message);
          setProcessing(false);
          toast.error(message || 'Erro no processamento');
        }
      } catch (error) {
        console.error('Erro ao processar mensagem WebSocket:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('Erro no WebSocket:', error);
      toast.error('Erro na conexão WebSocket');
    };
    
    ws.onclose = () => {
      console.log('WebSocket desconectado');
    };
  };

  const simulateFileDownload = () => {
    setIsDownloading(true);
    setFileDownloadProgress(0);
    
    const interval = setInterval(() => {
      setFileDownloadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsDownloading(false);
            setFileDownloadProgress(0);
          }, 1000);
          return 100;
        }
        return prev + 5;
      });
    }, 200);
  };

  // Cleanup ao fechar modal
  useEffect(() => {
    if (!isOpen) {
      setKeysList('');
      setProcessing(false);
      setTaskProgress(0);
      setTaskStage('');
      setTaskStats([]);
      setTaskLogs([]);
      setUploadedFiles([]);
      setDownloadLink('');
      setCurrentOutputId(null);
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Só remove se estiver saindo do modal completamente
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e, append = false) => {
    const files = Array.from(e.target.files);
    handleFiles(files, append);
    // Resetar input para permitir selecionar o mesmo arquivo novamente
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    
    // Reconstruir keysList sem o arquivo removido
    if (newFiles.length === 0) {
      setKeysList('');
    }
    
    toast.info('Arquivo removido da lista');
  };

  const handleClearAll = () => {
    setUploadedFiles([]);
    setKeysList('');
    toast.info('Todos os arquivos foram removidos');
  };

  const handleFiles = async (files, append = false) => {
    if (files.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];
    let totalLines = 0;
    let newContent = append ? keysList : '';

    for (const file of files) {
      const isTxt = file.name.toLowerCase().endsWith('.txt');

      if (!isTxt) {
        invalidFiles.push(file.name);
        continue;
      }

      try {
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim().length > 0);
        
        // Adicionar conteúdo
        if (newContent && !newContent.endsWith('\n')) {
          newContent += '\n';
        }
        newContent += text;
        
        totalLines += lines.length;
        validFiles.push({
          name: file.name,
          size: file.size,
          lines: lines.length,
          loadedAt: new Date().toISOString()
        });
        
        console.log(`Arquivo carregado: ${file.name} (${lines.length} linhas)`);
      } catch (error) {
        console.error('Erro ao ler arquivo:', error);
        invalidFiles.push(file.name);
      }
    }

    // Atualizar estado
    if (validFiles.length > 0) {
      setKeysList(newContent);
      setUploadedFiles(prev => append ? [...prev, ...validFiles] : validFiles);
      
      const fileWord = validFiles.length === 1 ? 'arquivo' : 'arquivos';
      const lineWord = totalLines === 1 ? 'linha' : 'linhas';
      toast.success(
        `${validFiles.length} ${fileWord} carregado${validFiles.length !== 1 ? 's' : ''} com sucesso! ${totalLines} ${lineWord} encontrada${totalLines !== 1 ? 's' : ''}.`,
        { autoClose: 3000 }
      );
    }

    // Mostrar erros
    if (invalidFiles.length > 0) {
      toast.error(
        `${invalidFiles.length} arquivo${invalidFiles.length !== 1 ? 's' : ''} inválido${invalidFiles.length !== 1 ? 's' : ''}: ${invalidFiles.join(', ')}`,
        { autoClose: 5000 }
      );
    }
  };

  const handleDownloadClick = async () => {
    if (!keysList.trim()) {
      toast.error('Por favor, insira ao menos uma chave');
      return;
    }
    
    const projectId = localStorage.getItem('vidal_selected_project');
    if (!projectId) {
      toast.error('Nenhum projeto selecionado');
      return;
    }

    // Processar chaves (validar 44 dígitos numéricos)
    const keys = keysList
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length === 44 && /^\d+$/.test(k));

    if (keys.length === 0) {
      toast.error('Nenhuma chave válida encontrada (devem ter 44 dígitos numéricos)');
      return;
    }

    console.log(`[DownloadKeysModal] ${keys.length} chaves válidas para download`);

    // Resetar estado
    setProcessing(true);
    setTaskProgress(0);
    setTaskStage('Iniciando...');
    setTaskStats([]);
    setTaskLogs([]);

    try {
      // Conectar WebSocket ANTES de enviar requisição
      connectWebSocket(projectId);
      
      // Aguardar 2s para conexão estabelecer
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Enviar para backend
      const response = await api.post('/xml-download/upload', {
        keys
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        toast.success(`Processamento iniciado para ${keys.length} chaves`);
      }
    } catch (error) {
      console.error('Erro ao iniciar download:', error);
      toast.error(error.response?.data?.error || 'Erro ao iniciar processamento');
      setProcessing(false);
      
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  };

  const handleClose = () => {
    if (!processing) {
      setKeysList('');
      setTaskProgress(0);
      setTaskStage('');
      setTaskStats([]);
      setTaskLogs([]);
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      onClose();
    }
  };
  
  const handleDownloadZip = async () => {
    try {
      setIsDownloading(true);
      setFileDownloadProgress(0);
      
      const projectId = localStorage.getItem('vidal_selected_project');
      
      // Usar rota diferente baseada no tipo de processamento
      let response;
      if (currentTaskType === 'download_xml_filter') {
        // Para filtros, usar outputId armazenado
        if (!currentOutputId) {
          throw new Error('OutputId não encontrado');
        }
        
        response = await api.get(`/xml-download/download-filter-zip`, {
          params: { outputId: currentOutputId },
          responseType: 'blob',
          timeout: 600000, // 10 min para ZIPs grandes
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
            setFileDownloadProgress(percentCompleted);
          }
        });
      } else {
        // Para chaves, usar rota antiga
        response = await api.get(`/xml-download/download-zip`, {
          params: { projeto: projectId },
          responseType: 'blob',
          timeout: 600000, // 10 min para ZIPs grandes
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
            setFileDownloadProgress(percentCompleted);
          }
        });
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'xmls.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download concluído!');
      
      setTimeout(() => {
        setIsDownloading(false);
        setFileDownloadProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
      console.error('Task type:', currentTaskType, 'OutputId:', currentOutputId);
      
      // Tentar ler mensagem de erro do blob response
      let errorMsg = 'Erro ao baixar arquivo ZIP';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.error || errorMsg;
        } catch (_) {}
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      setIsDownloading(false);
      setFileDownloadProgress(0);
    }
  };

  const showInputArea = !processing && taskProgress === 0;
  const showProcessingArea = processing || taskProgress > 0;

  const keysCount = keysList.split('\n').filter(k => k.trim().length > 0).length;

  // Funções auxiliares para filtros
  const toggleModelo = (value) => {
    setModelo((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const addCfop = (codigo) => {
    if (!cfopList.includes(codigo)) {
      setCfopList((prev) => [...prev, codigo]);
    }
    setCfopInput('');
  };

  const removeCfop = (codigo) => {
    setCfopList((prev) => prev.filter((c) => c !== codigo));
  };

  const handleCfopKeyDown = (e) => {
    if (e.key === 'Enter' && cfopInput.trim()) {
      addCfop(cfopInput.trim());
    }
  };

  const hasFilters = dataInicio || dataFim || modelo.length > 0 || cfopList.length > 0 || direcao || status;

  const handleFilterSubmit = async () => {
    if (!hasFilters) {
      toast.error('Selecione ao menos um filtro');
      return;
    }
    
    try {
      setProcessing(true);
      setTaskProgress(0);
      setTaskStage('Inicializando');
      setTaskLogs([]);
      setTaskStats([]);
      
      const projectId = localStorage.getItem('vidal_selected_project');
      
      // Preparar filtros
      const filters = {};
      if (dataInicio) filters.dataInicio = dataInicio;
      if (dataFim) filters.dataFim = dataFim;
      if (modelo.length > 0) filters.modelo = modelo;
      if (cfopList.length > 0) filters.cfop = cfopList;
      if (direcao) filters.direcao = direcao;
      if (status) filters.status = status;
      
      console.log('Enviando filtros:', filters);
      
      // Enviar para API
      const response = await api.post('/xml-download/download-by-filters', {
        filters
      });
      
      const { outputId } = response.data;
      console.log('Download por filtros iniciado, outputId:', outputId);
      
      // Armazenar outputId para usar no download
      setCurrentOutputId(outputId);
      
      // Conectar WebSocket para receber progresso
      connectWebSocket(projectId, 'download_xml_filter');
      
      toast.success('Processamento iniciado!');
    } catch (error) {
      console.error('Erro ao iniciar download por filtros:', error);
      toast.error('Erro ao iniciar processamento');
      setProcessing(false);
    }
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="title-block">
              <span className="eyebrow">DOWNLOAD DE XMLS · SEFAZ</span>
              <h2>{activeTab === 'filter' ? 'Por Filtros' : 'Por Chaves de Acesso'}</h2>
            </div>
          </div>
          <button className="close-btn" onClick={handleClose} disabled={processing} title="Fechar">
            <FiX />
          </button>
        </ModalHeader>

        {!processing && taskProgress < 100 && (
          <TabsRow>
            <button 
              className={`tab ${activeTab === 'keys' ? 'active' : ''}`}
              onClick={() => setActiveTab('keys')}
            >
              <FiKey />
              <span>Chaves</span>
            </button>
            <button 
              className={`tab ${activeTab === 'filter' ? 'active' : ''}`}
              onClick={() => setActiveTab('filter')}
            >
              <FiFilter />
              <span>Filtros</span>
            </button>
          </TabsRow>
        )}

        {/* Header Período + Progresso Geral (durante processamento) */}
        {(processing || taskProgress >= 100) && (
          <DLPeriodHeader>
            <DLSectionTitle>
              {activeTab === 'filter' ? 'Download por Filtros' : 'Download por Chaves'}
            </DLSectionTitle>
            {activeTab === 'filter' && dataInicio && dataFim && (
              <DLPeriodBadge>
                <FiCalendar />
                {dataInicio} — {dataFim}
              </DLPeriodBadge>
            )}
            {activeTab === 'keys' && keysCount > 0 && (
              <DLPeriodBadge>
                <FiKey />
                {keysCount} chave{keysCount !== 1 ? 's' : ''}
              </DLPeriodBadge>
            )}
            <DLProgressSection>
              <DLProgressInfo>
                <DLProgressLabel>Progresso Geral</DLProgressLabel>
                <DLProgressCount>{Math.round(isDownloading ? fileDownloadProgress : taskProgress)}%</DLProgressCount>
              </DLProgressInfo>
              <DLProgressBarContainer>
                <DLProgressBarFill $progress={isDownloading ? fileDownloadProgress : taskProgress} />
              </DLProgressBarContainer>
            </DLProgressSection>
          </DLPeriodHeader>
        )}

        <ModalBody
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {showInputArea && activeTab === 'keys' && (
            <StepWrapper>
              <ScrollArea>
              <InputArea $hasFiles={uploadedFiles.length > 0}>
                <div className="input-header">
                  <span className="input-label">LISTA DE CHAVES DE ACESSO</span>
                  {keysCount > 0 && (
                    <span className="keys-count">{keysCount} chave{keysCount !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <textarea
                  ref={textareaRef}
                  placeholder="Cole aqui as chaves de acesso dos XMLs (uma por linha)&#10;&#10;Exemplo:&#10;35240512345678901234550010000123451234567890&#10;35240512345678901234550010000123461234567891&#10;35240512345678901234550010000123471234567892"
                  value={keysList}
                  onChange={(e) => setKeysList(e.target.value)}
                  rows={uploadedFiles.length > 0 ? 6 : 12}
                />
              </InputArea>

              {/* Arquivos Carregados */}
              {uploadedFiles.length > 0 && (
                <FilesList>
                  {uploadedFiles.map((file, index) => (
                    <FileItem key={index}>
                      <div className="file-icon">
                        <FiFileText />
                      </div>
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-meta">
                          {file.lines.toLocaleString()} linha{file.lines !== 1 ? 's' : ''} • {(file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button 
                        className="remove-file"
                        onClick={() => handleRemoveFile(index)}
                        title="Remover arquivo"
                      >
                        <FiX />
                      </button>
                    </FileItem>
                  ))}
                </FilesList>
              )}

              <Dropzone
                className={`${isDragging ? 'active' : ''} ${uploadedFiles.length > 0 ? 'has-files' : ''}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, uploadedFiles.length > 0)}
                />
                <div className="drop-icon">
                  {uploadedFiles.length > 0 ? <FiCheckCircle /> : (isDragging ? <IoCloudDownloadOutline /> : <FiFileText />)}
                </div>
                <div className="drop-text">
                  {uploadedFiles.length > 0 ? (
                    <>
                      <span className="drop-eyebrow">SELECIONADOS</span>
                      <h4>{uploadedFiles.length} arquivo{uploadedFiles.length > 1 ? 's' : ''}</h4>
                      <p>Clique para adicionar mais ou arraste novos</p>
                    </>
                  ) : (
                    <>
                      <span className="drop-eyebrow">ARRASTE OU CLIQUE</span>
                      <h4>{isDragging ? 'Solte os arquivos aqui' : 'Adicionar arquivos TXT'}</h4>
                      <p>Aceita .txt · múltiplos arquivos</p>
                    </>
                  )}
                </div>
              </Dropzone>
              </ScrollArea>
              <StepFooter>
                <SubmitButton disabled={keysCount === 0} onClick={handleDownloadClick}>
                  Processar Download
                </SubmitButton>
              </StepFooter>
            </StepWrapper>
          )}

          {showInputArea && activeTab === 'filter' && (
            <StepWrapper>
              <ScrollArea>
              <FilterGroup>
                <FilterSectionHeader>
                  <FiCalendar />
                  <h3>Período</h3>
                </FilterSectionHeader>
                <DateInputGroup>
                  <div className="input-wrapper">
                    <label>De</label>
                    <DateInput
                      type="date"
                      value={dataInicio}
                      onChange={(e) => setDataInicio(e.target.value)}
                    />
                  </div>
                  <div className="separator">até</div>
                  <div className="input-wrapper">
                    <label>Até</label>
                    <DateInput
                      type="date"
                      value={dataFim}
                      onChange={(e) => setDataFim(e.target.value)}
                    />
                  </div>
                </DateInputGroup>
              </FilterGroup>

              <FilterGroup>
                <FilterSectionHeader>
                  <FiFileText />
                  <h3>Tipo de Documento</h3>
                </FilterSectionHeader>
                <FilterBadges>
                  {[{ label: 'NF-e', value: '55' }, { label: 'CT-e', value: '57' }].map(({ label, value }) => (
                    <FilterBadge
                      key={value}
                      $active={modelo.includes(value)}
                      onClick={() => toggleModelo(value)}
                    >
                      {label}
                    </FilterBadge>
                  ))}
                  {modelo.length > 0 && (
                    <FilterBadge onClick={() => setModelo([])}>
                      Limpar
                    </FilterBadge>
                  )}
                </FilterBadges>
              </FilterGroup>

              <FilterGroup>
                <FilterSectionHeader>
                  <MdOutlineNumbers />
                  <h3>CFOP</h3>
                </FilterSectionHeader>
                <FilterInput
                  type="text"
                  placeholder="Digite um CFOP (ex: 5102) e pressione Enter"
                  value={cfopInput}
                  onChange={(e) => setCfopInput(e.target.value)}
                  onKeyDown={handleCfopKeyDown}
                />
                {cfopList.length > 0 && (
                  <FilterBadges style={{ marginTop: '0.8em' }}>
                    {cfopList.map((c) => (
                      <FilterBadge key={c} $active onClick={() => removeCfop(c)}>
                        {c} <FiX style={{ marginLeft: '0.3em', fontSize: '0.85em' }} />
                      </FilterBadge>
                    ))}
                  </FilterBadges>
                )}
              </FilterGroup>

              <FilterGroup>
                <FilterSectionHeader>
                  <FiRepeat />
                  <h3>Direção</h3>
                </FilterSectionHeader>
                <FilterBadges>
                  {[
                    { label: 'Emissão Própria', value: 'propria' },
                    { label: 'Terceiros', value: 'terceiros' },
                  ].map(({ label, value }) => (
                    <FilterBadge
                      key={value}
                      $active={direcao === value}
                      onClick={() => setDirecao(direcao === value ? null : value)}
                    >
                      {label}
                    </FilterBadge>
                  ))}
                </FilterBadges>
              </FilterGroup>

              <FilterGroup>
                <FilterSectionHeader>
                  <FiCheckCircle />
                  <h3>Status</h3>
                </FilterSectionHeader>
                <FilterBadges>
                  {['Autorizada', 'Cancelada'].map((s) => (
                    <FilterBadge
                      key={s}
                      $active={status === s}
                      onClick={() => setStatus(status === s ? null : s)}
                    >
                      {s}
                    </FilterBadge>
                  ))}
                </FilterBadges>
              </FilterGroup>

              </ScrollArea>
              <StepFooter>
                <SubmitButton disabled={!hasFilters} onClick={handleFilterSubmit}>
                  Download
                </SubmitButton>
              </StepFooter>
            </StepWrapper>
          )}

          {(processing || taskProgress === 100) && (
            <ProcessingSection>
              <ProcessingGrid>
                {/* Painel de Tarefas */}
                <TasksPanel>
                  <PanelHeader>
                    <PanelLabel>TAREFAS</PanelLabel>
                    <PanelBadge $done={taskProgress >= 100 && !processing}>
                      {processing || isDownloading
                        ? `${(() => {
                            const items = [];
                            taskStats.forEach((stat, idx) => items.push({ id: `stat-${idx}`, status: 'completed' }));
                            if (processing || isDownloading) items.push({ id: 'current', status: 'running' });
                            if (taskProgress >= 100 && !processing && !isDownloading) items.push({ id: 'done', status: 'completed' });
                            return items.filter(t => t.status === 'completed').length;
                          })()}/${(() => {
                            const items = [];
                            taskStats.forEach((stat, idx) => items.push({ id: `stat-${idx}` }));
                            if (processing || isDownloading) items.push({ id: 'current' });
                            if (taskProgress >= 100 && !processing && !isDownloading) items.push({ id: 'done' });
                            return items.length;
                          })()}`
                        : 'Concluído'
                      }
                    </PanelBadge>
                  </PanelHeader>
                  <TasksList>
                    {/* Stats as completed items */}
                    {taskStats.map((stat, idx) => (
                      <TaskItem key={`stat-${idx}`} $status="completed">
                        <TaskStatusDot $status="completed"><GoCheck /></TaskStatusDot>
                        <RTaskContent>
                          <TaskName $status="completed">
                            {stat.label}: {typeof stat.value === 'string' ? stat.value : stat.value.toLocaleString('pt-BR')}
                          </TaskName>
                        </RTaskContent>
                      </TaskItem>
                    ))}
                    {/* Current stage */}
                    {(processing || isDownloading) && (
                      <TaskItem $status="running">
                        <TaskStatusDot $status="running"><GoSync className="spinning" /></TaskStatusDot>
                        <RTaskContent>
                          <TaskName $status="running">
                            {isDownloading ? 'Baixando arquivo...' : (taskStage || 'Processando...')}
                          </TaskName>
                          {(isDownloading ? fileDownloadProgress : taskProgress) > 0 && (
                            <TaskProgressBar>
                              <TaskProgressFill $progress={isDownloading ? fileDownloadProgress : taskProgress} />
                            </TaskProgressBar>
                          )}
                        </RTaskContent>
                        {(isDownloading ? fileDownloadProgress : taskProgress) > 0 && (
                          <TaskPercent>{Math.round(isDownloading ? fileDownloadProgress : taskProgress)}%</TaskPercent>
                        )}
                      </TaskItem>
                    )}
                    {/* Completion */}
                    {taskProgress >= 100 && !processing && !isDownloading && (
                      <TaskItem $status="completed">
                        <TaskStatusDot $status="completed"><GoCheck /></TaskStatusDot>
                        <RTaskContent>
                          <TaskName $status="completed">Download concluído!</TaskName>
                        </RTaskContent>
                      </TaskItem>
                    )}
                  </TasksList>
                </TasksPanel>

                {/* Painel de Logs */}
                <RLogsPanel>
                  <PanelHeader>
                    <PanelLabel>TERMINAL</PanelLabel>
                    <RLogCounter>{taskLogs.length} linhas</RLogCounter>
                  </PanelHeader>
                  <RLogsEditor>
                    {taskLogs.length === 0 ? (
                      <REmptyLogs>
                        <span>$</span> aguardando saída...
                      </REmptyLogs>
                    ) : (
                      [...taskLogs].reverse().map((log, index) => (
                        <RLogLine key={index} $type={log.type}>
                          <RLogLineNumber>{taskLogs.length - index}</RLogLineNumber>
                          <RLogTimestamp>{log.timestamp}</RLogTimestamp>
                          <RLogText $type={log.type}>{log.message}</RLogText>
                        </RLogLine>
                      ))
                    )}
                  </RLogsEditor>
                </RLogsPanel>
              </ProcessingGrid>
            </ProcessingSection>
          )}
        </ModalBody>

        {taskProgress >= 100 && !processing && (
          <RModalFooter>
            <RFooterButton className="cancel" onClick={handleClose}>
              Fechar
            </RFooterButton>
            <RFooterButton className="primary" onClick={handleDownloadZip} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <FiClock className="spinner" />
                  Baixando...
                </>
              ) : (
                <>
                  <FiDownload />
                  Baixar Arquivo ZIP
                </>
              )}
            </RFooterButton>
          </RModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}


// ============= STYLED COMPONENTS =============

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const spinKf = keyframes`
  to { transform: rotate(360deg); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: 0.6em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(12px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};

  .spin { animation: ${spinKf} 1s linear infinite; }
`;

const ModalContent = styled.div`
  position: relative;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  width: 100%;
  max-width: 640px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 1px 0 0 rgba(255, 255, 255, 0.04) inset,
    0 24px 64px rgba(0, 0, 0, 0.45),
    0 8px 16px rgba(0, 0, 0, 0.18);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

  /* Hairline highlight no topo (assinatura visual) */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
    pointer-events: none;
    z-index: 2;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .header-left {
    flex: 1;
    min-width: 0;
  }

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    .eyebrow {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.4;
    }

    h2, h3 {
      margin: 2px 0 0 0;
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
    }
  }

  .close-btn, .modal-close {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    opacity: 0.65;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 1em; }

    &:hover:not(:disabled) {
      opacity: 1;
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    &:disabled {
      opacity: 0.25;
      cursor: not-allowed;
    }
  }

  /* manter compat com .header-actions antigo se houver */
  .header-actions button {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    opacity: 0.65;
    flex-shrink: 0;

    svg { font-size: 1em; }

    &:hover:not(:disabled) {
      opacity: 1;
      border-color: ${({ theme }) => hairline(theme, true)};
    }
  }
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 18px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  button {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px 11px;
    background: transparent;
    border: none;
    border-radius: 0;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    ${mono};
    font-size: 0.62em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    white-space: nowrap;

    svg { font-size: 0.95em; opacity: 0.85; }

    /* underline animado */
    &::after {
      content: '';
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: -1px;
      height: 1px;
      background: ${tokens.brand};
      transform: scaleX(0);
      transform-origin: center;
      transition: transform ${tokens.durBase} ${tokens.easeOut};
    }

    &:hover { opacity: 0.85; }

    &.active {
      color: ${tokens.brand};
      opacity: 1;

      svg { opacity: 1; }
      &::after { transform: scaleX(1); }
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.25;
    }
  }
`;

/* ModalBody é apenas o frame; o scroll fica no ScrollArea de cada step */
const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StepWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const StepFooter = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }
`;

const InputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .input-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .input-label {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.5;
  }

  .keys-count {
    ${mono};
    padding: 2px 8px;
    background: ${tokens.brandSoft};
    color: ${tokens.brand};
    border: 1px solid ${tokens.brandRing};
    border-radius: ${tokens.radiusPill};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  textarea {
    width: 100%;
    min-height: 140px;
    padding: 12px 14px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    font-family: ${tokens.fontMono};
    font-size: 0.78em;
    line-height: 1.55;
    resize: vertical;
    outline: none;
    transition: border-color ${tokens.durBase} ${tokens.easeOut};

    &::placeholder { opacity: 0.4; }
    &:focus { border-color: ${tokens.brandRing}; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }

  .file-icon {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.brandSoft};
    border: 1px solid ${tokens.brandRing};
    border-radius: ${tokens.radiusSm};
    color: ${tokens.brand};
    flex-shrink: 0;

    svg { font-size: 0.95em; }
  }

  .file-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .file-name {
      font-size: 0.8em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-size, .file-meta {
      ${mono};
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;
    }
  }

  .file-status {
    ${mono};
    padding: 2px 7px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.58em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid;

    &.success {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border-color: ${tokens.brandRing};
    }
    &.error {
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border-color: rgba(239, 68, 68, 0.22);
    }
  }

  .file-remove {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${tokens.radiusSm};
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    svg { font-size: 0.85em; }

    &:hover {
      background: ${tokens.dangerSoft};
      border-color: rgba(239, 68, 68, 0.3);
      color: ${tokens.danger};
      opacity: 1;
    }

    &:disabled { opacity: 0.2; cursor: not-allowed; }
  }
`;

const Dropzone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 32px 24px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px dashed ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  cursor: pointer;
  text-align: center;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover {
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};

    .drop-icon {
      border-color: ${tokens.brand};
      background: ${tokens.brandSoft};
    }
  }

  &.active,
  &.dragging {
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};
    box-shadow: 0 0 0 4px ${tokens.brandRing};

    .drop-icon {
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
    }
  }

  &.has-files {
    border-style: solid;
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};

    .drop-icon {
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
    }
  }

  .drop-icon {
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 50%;
    color: ${tokens.brand};
    transition: all ${tokens.durBase} ${tokens.easeOut};

    svg { font-size: 1.5em; }
  }

  .drop-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .drop-eyebrow {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.5;
    }

    h4 {
      margin: 4px 0 2px 0;
      font-size: 0.95em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;
    }
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};

  svg {
    color: ${tokens.brand};
    opacity: 0.85;
    font-size: 0.95em;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 0.78em;
    line-height: 1.5;
    color: ${({ theme }) => theme.text};
    opacity: 0.75;
    letter-spacing: -0.005em;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 16px;
    font-size: 0.74em;
    line-height: 1.55;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;

    li { margin-bottom: 2px; }
  }

  strong {
    color: ${({ theme }) => theme.text};
    opacity: 1;
  }
`;

/* ModalFooter mantido como alias do StepFooter pra retrocompat (tab que ainda usa) */
const ModalFooter = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 28px;
  background: ${tokens.brand};
  color: white;
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.85em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  white-space: nowrap;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.25);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FilterSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${eyebrow};
  font-size: 0.58em;
  opacity: 0.55;

  svg { font-size: 1em; opacity: 0.8; }

  h4 {
    margin: 0;
    ${mono};
    font-size: 1em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    label {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.55;
    }
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontMono};
  font-size: 0.78em;
  letter-spacing: 0.01em;
  outline: none;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &::placeholder { opacity: 0.4; }
  &:focus { border-color: ${tokens.brandRing}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  letter-spacing: -0.005em;
  outline: none;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &::placeholder { opacity: 0.4; }
  &:focus { border-color: ${tokens.brandRing}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const FilterBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const FilterBadge = styled.button`
  ${mono};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 4px 4px 10px;
  background: ${tokens.brandSoft};
  color: ${tokens.brand};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusPill};
  font-size: 0.62em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    font-size: 0.95em;
    transition: background ${tokens.durBase} ${tokens.easeOut};
  }

  &:hover svg {
    background: rgba(16, 185, 129, 0.18);
  }
`;

const DLPeriodHeader = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const DLSectionTitle = styled.h4`
  ${eyebrow};
  margin: 0;
  font-size: 0.6em;
  opacity: 0.55;
`;

const DLPeriodBadge = styled.div`
  ${mono};
  align-self: flex-start;
  padding: 4px 10px;
  background: ${tokens.brandSoft};
  color: ${tokens.brand};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusPill};
  font-size: 0.66em;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg { font-size: 0.95em; }
`;

const DLProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0 4px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
`;

const DLProgressInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DLProgressLabel = styled.span`
  ${mono};
  font-size: 0.62em;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.text};
  opacity: 0.55;
`;

const DLProgressCount = styled.span`
  ${mono};
  font-size: 0.74em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
`;

const DLProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => surface(theme, 2)};
  border-radius: ${tokens.radiusPill};
  overflow: hidden;
`;

const DLProgressBarFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${tokens.brand};
  border-radius: ${tokens.radiusPill};
  transition: width 0.3s ${tokens.easeOut};
`;

const ProcessingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ProcessingGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 1)};
`;

const PanelLabel = styled.span`
  ${eyebrow};
  font-size: 0.58em;
  opacity: 0.6;
`;

const PanelBadge = styled.span`
  ${mono};
  padding: 2px 8px;
  border-radius: ${tokens.radiusPill};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid;

  ${({ $done }) =>
    $done
      ? css`
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        `
      : css`
          background: rgba(59, 130, 246, 0.10);
          color: #3b82f6;
          border-color: rgba(59, 130, 246, 0.25);
        `}
`;

const RLogCounter = styled.span`
  ${mono};
  font-size: 0.6em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  letter-spacing: 0.02em;
`;

const TasksPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
`;

const TasksList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 8px 16px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
`;

const TaskStatusDot = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) => {
    if ($status === 'completed') return tokens.brand;
    if ($status === 'running') return 'rgba(59, 130, 246, 0.18)';
    if ($status === 'error') return tokens.dangerSoft;
    return 'transparent';
  }};
  border: 1px solid ${({ $status }) => {
    if ($status === 'completed') return tokens.brand;
    if ($status === 'running') return 'rgba(59, 130, 246, 0.45)';
    if ($status === 'error') return 'rgba(239, 68, 68, 0.45)';
    return 'rgba(255,255,255,0.18)';
  }};
  color: ${({ $status }) => $status === 'completed' ? '#fff' : 'transparent'};

  svg { font-size: 0.7em; }
`;

const RTaskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TaskName = styled.div`
  ${mono};
  font-size: 0.7em;
  letter-spacing: 0.01em;
  color: ${({ theme, $status }) => {
    if ($status === 'completed') return theme.text;
    if ($status === 'running') return theme.text;
    return theme.text;
  }};
  opacity: ${({ $status }) => ($status === 'pending' ? 0.45 : 0.85)};
  ${({ $status }) => $status === 'completed' && 'text-decoration: line-through; opacity: 0.55;'}
`;

const TaskProgressBar = styled.div`
  width: 100%;
  height: 2px;
  background: rgba(59, 130, 246, 0.10);
  border-radius: ${tokens.radiusPill};
  overflow: hidden;
`;

const TaskProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: rgba(59, 130, 246, 0.6);
  border-radius: ${tokens.radiusPill};
  transition: width 0.4s ${tokens.easeOut};
`;

const TaskPercent = styled.div`
  ${mono};
  font-size: 0.6em;
  color: rgba(59, 130, 246, 0.7);
  letter-spacing: 0.02em;
`;

const RLogsPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const RLogsEditor = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => surface(theme, 2)};
  padding: 8px 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const REmptyLogs = styled.div`
  padding: 24px 16px;
  ${mono};
  text-align: center;
  font-size: 0.7em;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  letter-spacing: 0.02em;
`;

const RLogLine = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1px 0;
  ${mono};
  font-size: 0.66em;
  line-height: 1.5;
  letter-spacing: 0.01em;

  &:hover {
    background: ${({ theme }) => surface(theme, 1)};
  }
`;

const RLogLineNumber = styled.span`
  width: 38px;
  text-align: right;
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.3;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => hairline(theme)};
`;

const RLogTimestamp = styled.span`
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
  flex-shrink: 0;
`;

const RLogText = styled.span`
  flex: 1;
  padding-right: 12px;
  word-break: break-word;
  color: ${({ theme }) => theme.text};
  opacity: 0.85;

  &.error   { color: ${tokens.danger}; }
  &.warn    { color: ${tokens.warn}; }
  &.success { color: ${tokens.brand}; }
  &.info    { opacity: 0.7; }
  &.debug   { opacity: 0.45; }
`;

const RModalFooter = styled.div`
  flex-shrink: 0;
  padding: 14px 20px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const RFooterButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  letter-spacing: -0.005em;
  white-space: nowrap;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme)};
  opacity: 0.85;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
    opacity: 1;
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }

  ${({ $primary }) =>
    $primary &&
    css`
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
      opacity: 1;

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    `}
`;

const CompletionContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 28px 20px;

  .completion-icon {
    width: 64px;
    height: 64px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.brandSoft};
    border: 1px solid ${tokens.brandRing};
    border-radius: 50%;
    color: ${tokens.brand};

    svg { font-size: 1.7em; }
  }

  h3 {
    margin: 0;
    ${mono};
    font-size: 0.85em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    ${mono};
    font-size: 0.74em;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    letter-spacing: 0.01em;
    text-align: center;
    max-width: 360px;
    line-height: 1.55;
  }
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};

  .info-title {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    svg { font-size: 1em; opacity: 0.8; }
  }

  .info-text, p {
    margin: 0;
    font-size: 0.78em;
    line-height: 1.55;
    color: ${({ theme }) => theme.text};
    opacity: 0.75;
    letter-spacing: -0.005em;
  }

  ul {
    margin: 0;
    padding-left: 16px;
    font-size: 0.74em;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    line-height: 1.55;

    li { margin-bottom: 2px; }
  }
`;

const CompletionStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
`;

const StackedStatCard = styled.div`
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .stat-icon {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    margin-bottom: 4px;

    svg { font-size: 0.95em; }
  }

  .stat-value {
    ${mono};
    font-size: 1.15em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .stat-label {
    ${eyebrow};
    font-size: 0.55em;
    opacity: 0.5;
  }

  &.success .stat-value { color: ${tokens.brand}; }
  &.success .stat-icon { color: ${tokens.brand}; opacity: 0.65; }

  &.warning .stat-value { color: ${tokens.warn}; }
  &.warning .stat-icon { color: ${tokens.warn}; opacity: 0.65; }

  &.danger .stat-value { color: ${tokens.danger}; }
  &.danger .stat-icon { color: ${tokens.danger}; opacity: 0.65; }
`;

const DownloadProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
`;

const DownloadProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .label {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    svg { font-size: 1em; opacity: 0.8; }
  }

  .pct {
    ${mono};
    font-size: 0.85em;
    font-weight: 500;
    color: ${tokens.brand};
    letter-spacing: -0.01em;
  }
`;

const DownloadProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => surface(theme, 2)};
  border-radius: ${tokens.radiusPill};
  overflow: hidden;

  > div {
    height: 100%;
    background: ${tokens.brand};
    border-radius: ${tokens.radiusPill};
    transition: width 0.3s ${tokens.easeOut};
  }
`;

const DownloadProgressFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  ${mono};
  font-size: 0.62em;
  color: ${({ theme }) => theme.text};
  opacity: 0.55;
  letter-spacing: 0.01em;
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  background: ${tokens.brand};
  color: white;
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  font-family: ${tokens.fontSans};
  font-size: 0.82em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  letter-spacing: -0.005em;
  white-space: nowrap;

  svg { font-size: 1em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
